'use client'

import debounce from 'debounce-promise'
import React, { SyntheticEvent, useEffect, useId, useState } from 'react'
import type { ActionMeta, MultiValue, SingleValue } from 'react-select'
import { SelectComponentsConfig, Props as StateManagerProps } from 'react-select'
import AsyncSelect from 'react-select/async'
import { useInputWrapper } from '../../../hooks/useInputWrapper'
import { Text } from '../../atoms'
import {
    ClearIndicatorReplacement,
    DropdownIndicatorReplacement,
    MultiLineOption,
    SingleValueMultiLineComponent,
    SingleValueTagComponent,
} from './Components'
import { OptionReplacement } from './Components/OptionReplacement'
import { SelectOption, GroupedSelectOption, AsyncSingleSelectInputProps } from '../../../types/SelectInput'
import {
    getSelectInputClassNames,
    getSelectContainerClassNames,
    getCustomSelectInputStyles,
} from './Helpers/select-helpers'
import { formatGroupLabel, LoadingComponent } from './Components/AsyncComponent'
import { loadAsyncQueryOptions } from './Helpers/async-select-helpers'
import { exists } from '../../../../../MyStichtingOpenHelpers/src/static'

export const AsyncSingleSelectInput = ({
    options = [],
    asyncQuery,
    asyncOptions,
    isClearable = true,
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultInputValue = undefined,
    noOptionsMessage = 'No results',
    isInline = false,
    className = '',
    placeholder,
    autoFocus = false,
    asyncMinimumCharacters = 3,
    menuPosition = 'fixed',
    ...props
}: AsyncSingleSelectInputProps) => {
    const { onBlurHandler, onFocusHandler, classes } = useInputWrapper(false, disabled)
    const generatedId = useId()
    const selectId = id ?? generatedId
    const [initialOptions, setInitialOptions] = useState<SelectOption[]>(options)
    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null)

    useEffect(() => {
        const hasValue = exists(value) && value !== ''

        if (!hasValue) {
            setSelectedOption(null)
            return
        }

        const matchingSelectedOption = options.find((option) => value === option?.value) ?? null
        if (matchingSelectedOption) {
            setSelectedOption(matchingSelectedOption)
            return
        }

        if (selectedOption?.value !== value) {
            setSelectedOption({
                value,
                label: value.toString(),
            })
        }
    }, [selectedOption?.value, value])

    const components: SelectComponentsConfig<SelectOption, boolean, GroupedSelectOption> = {
        DropdownIndicator: DropdownIndicatorReplacement,
        ClearIndicator: ClearIndicatorReplacement,
        ...(!showMultiLine && !showAsTag && { Option: OptionReplacement }),
    }

    if (showAsTag) components.SingleValue = SingleValueTagComponent
    if (showMultiLine) components.SingleValue = SingleValueMultiLineComponent

    const debouncedLoadOptions: (inputValue: string) => Promise<SelectOption[] | GroupedSelectOption[]> = debounce(
        (inputValue: string) =>
            loadAsyncQueryOptions(inputValue, {
                asyncMinimumCharacters,
                asyncQuery,
                isSearchable,
            }),
        250,
    )

    const loadAsyncOptions = (): Promise<SelectOption[]> => {
        const emptyResults = new Promise<SelectOption[]>((resolve) => {
            resolve([])
        })
        return asyncOptions ? asyncOptions() : emptyResults
    }

    const selectProps: StateManagerProps<SelectOption, boolean, GroupedSelectOption> = {
        instanceId: selectId,
        id: selectId,
        inputId: selectId,
        isClearable,
        className: getSelectInputClassNames({ isInline, isMulti: false, isSearchable, showAsTag, readOnly }),
        classNamePrefix: 'select-input',
        isDisabled: disabled,
        closeMenuOnSelect: true,
        hideSelectedOptions: false,
        isSearchable: !readOnly ? isSearchable : false,
        onBlur: (event: SyntheticEvent) => {
            if (onBlur) onBlur(event)
            onBlurHandler()
        },
        menuPlacement: 'auto',
        components,
        styles: getCustomSelectInputStyles(showMultiLine, isSearchable),
        noOptionsMessage: () => <Text>{noOptionsMessage}</Text>,
        loadingMessage: LoadingComponent,
        ...(showMultiLine && { formatOptionLabel: MultiLineOption }),
        ...(readOnly && { menuIsOpen: false }),
        ...(placeholder ? { placeholder } : { placeholder: '' }),
        autoFocus,
    }

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
        >
            <AsyncSelect
                {...selectProps}
                options={initialOptions}
                cacheOptions
                loadOptions={isSearchable ? debouncedLoadOptions : loadAsyncOptions}
                formatGroupLabel={formatGroupLabel}
                onChange={(
                    newValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
                    actionMeta: ActionMeta<SelectOption>,
                ) => {
                    setSelectedOption((newValue as SelectOption) ?? null)

                    if (newValue == null || (Array.isArray(newValue) && newValue.length === 0)) {
                        setInitialOptions([])
                    }

                    if (onChange) onChange(newValue as SelectOption & SelectOption[], actionMeta)
                }}
                {...(!isSearchable && { defaultOptions: true })}
                defaultInputValue={defaultInputValue}
                onFocus={() => {
                    onFocusHandler()
                }}
                value={selectedOption}
                menuPosition={menuPosition}
                isOptionDisabled={(option: SelectOption) => !!option.disabled}
            />
        </div>
    )
}
